// "New since you last looked" for the investigation tabs. The server keeps
// one stamp per tab per member (investigation_members.seen_at) and returns
// both the count of newer items and the stamp itself; each tab highlights
// rows whose own timestamp beats the stamp, and the banner clears it.
import { SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";

export type TabKey = "brief" | "leads" | "calls" | "people" | "timeline";

export const TAB_NOUN: Record<TabKey, [string, string]> = {
  brief: ["change", "changes"],
  leads: ["new lead", "new leads"],
  calls: ["new call", "new calls"],
  people: ["new or updated mention", "new or updated mentions"],
  timeline: ["new event", "new events"],
};

export function describeUpdates(tab: TabKey, n: number, since?: string): string {
  const [one, many] = TAB_NOUN[tab];
  return `${n} ${n === 1 ? one : many}${since ? ` since ${formatSince(since)}` : ""}`;
}

export function formatSince(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }), hour: "numeric", minute: "2-digit" });
}

export function isNewSince(ts: string | Date | null | undefined, since: string | undefined): boolean {
  if (!ts || !since) return false;
  return new Date(ts).getTime() > new Date(since).getTime();
}

// Row treatment for something new: a soft blue wash and a ring that reads
// as "unread" without fighting the card's own hover/selection styles.
// "Mark as seen" flips `clearing` first, so the wash and ring ease off over
// CLEAR_MS before the stamp actually moves; the transition classes stay on
// the row so the removal animates rather than snaps.
export const CLEAR_MS = 650;
export const NEW_ROW_CLASS = "ring-1 ring-blue-200 dark:ring-blue-900/60 bg-blue-50/60 dark:bg-blue-900/20";
export const NEW_ROW_TRANSITION = "transition-[background-color,box-shadow,border-color] duration-[650ms] ease-out";

export function newRowClass(isNew: boolean, clearing?: boolean): string {
  return cn(NEW_ROW_TRANSITION, isNew && !clearing && NEW_ROW_CLASS);
}

// Small markers (pill, dot, rule) fade and shrink away while clearing.
export function clearingClass(clearing?: boolean): string {
  return cn("transition-[opacity,transform] duration-500 ease-out", clearing && "opacity-0 scale-75");
}

// Inline pill. While clearing it fades AND its column collapses to zero
// width (grid-template-columns 1fr → 0fr) with a negative margin eating the
// flex gap, so the badges to its right glide left instead of jumping when
// the pill finally unmounts.
export const NewPill: React.FC<{ className?: string; clearing?: boolean }> = ({ className, clearing }) => (
  <span className={cn("grid self-center transition-[grid-template-columns,margin] duration-[650ms] ease-out", clearing ? "grid-cols-[0fr] -mr-2" : "grid-cols-[1fr]", className)}>
    {/* 1px padding + matching negative margin keeps the ring inside the clip box. */}
    <span className="flex min-w-0 overflow-hidden p-px -m-px">
      <span className={cn("inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-900/60 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-px leading-4", clearingClass(clearing))}>New</span>
    </span>
  </span>
);

// The banner collapses via a grid-rows 1fr → 0fr transition (the same trick
// as the Leads dismiss and ConditionalFieldGroup) so the content below
// slides up with it; by the time the stamp moves and it unmounts, its
// height is already zero and nothing jumps. `className` carries the outer
// margin so that collapses too.
export const UpdatesBanner: React.FC<{ tab: TabKey; count: number; since?: string; onDismiss: () => void; clearing?: boolean; className?: string }> = ({ tab, count, since, onDismiss, clearing, className }) => {
  if (!count) return null;
  return (
    <div className={cn("grid transition-[grid-template-rows] duration-[650ms] ease-out", clearing ? "grid-rows-[0fr]" : "grid-rows-[1fr]")}>
    <div className="min-h-0 overflow-hidden">
    <div className={cn("flex items-center gap-3 rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50 dark:bg-blue-900/40 px-4 py-2.5 text-sm transition-[opacity,transform] duration-500 ease-out", clearing && "opacity-0 -translate-y-1", className)}>
      <SparklesIcon className="size-4 shrink-0 text-blue-600 dark:text-blue-300" />
      <p className="min-w-0 flex-1 text-base-content/90">
        <span className="font-semibold">{describeUpdates(tab, count)}</span>
        {since ? <span className="text-base-content/60"> since {formatSince(since)}. {tab === "brief" ? "Listed in the update log below" : "Highlighted below"} until you mark them seen.</span> : null}
      </p>
      <button type="button" onClick={onDismiss} disabled={clearing} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60" title="Clear the highlights">
        <XMarkIcon className="size-3.5" /> {clearing ? "Clearing…" : "Mark as seen"}
      </button>
    </div>
    </div>
    </div>
  );
};
